import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
  OnInit,
  inject,
  DestroyRef,
  ChangeDetectionStrategy,
} from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, pairwise, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
  searchPosts,
  toggleAdvancedSearch,
} from 'src/app/core/actions/feed.actions';
import { injectAddRecentSearchMutation } from 'src/app/api/queries/user.queries';
import { selectAdvancedSearchOpen } from 'src/app/core/reducers';

import { DimensionAttachTypeId, NomObjectClass } from 'src/app/core/constants';
import { Feed } from 'src/app/core/models/core.models';
import { DimensionAttachParams } from 'src/app/core/services/feed.service';
import {
  FeedApiService,
  QueryValidationResponse,
} from 'src/app/api/services/feed-api.service';
import { ObjectCreatorService } from 'src/app/core/services/object-creator.service';

import { AppState } from 'src/app/reducers';
import { EditorView, keymap } from '@codemirror/view';
import { Actions, ofType } from '@ngrx/effects';
import { createAndAttachDimensionSuccess } from '../../../core/actions/feed.actions';
import { AdvancedSearchService } from 'src/app/core/services/advanced-search.service';
import { QueryHintOverlayService } from '@services/query-hint-overlay.service';
import { SidebarService } from 'src/app/core/services/sidebar.service';

@Component({
  selector: 'app-advanced-search',
  templateUrl: './advanced-search.component.html',
  styleUrl: './advanced-search.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AdvancedSearchComponent
  implements AfterViewInit, OnDestroy, OnInit
{
  @ViewChild('resizableBox') resizableBox!: ElementRef<HTMLElement>;
  @ViewChild('queryEditor') queryEditor: any;

  @Input() feed: Feed;
  @Input() searchQuery: string;

  currentQuery: string;
  copiedToClipboard = false;
  componentId: string = 'advanced-search';

  advancedSearchOpened$: Observable<boolean>;
  sidebarOpened$: Observable<boolean>;
  sidebarWide$: Observable<boolean>;

  private ro?: ResizeObserver;
  private clampScheduled = false;

  // ✨ add destroy ref for safe unsubscription
  private destroyRef = inject(DestroyRef);
  private feedApi = inject(FeedApiService);
  private actions$ = inject(Actions);
  private advancedSearchService = inject(AdvancedSearchService);
  public queryHintsOverlayService = inject(QueryHintOverlayService);
  private sidebarService = inject(SidebarService);
  private readonly addRecentSearchMutation = injectAddRecentSearchMutation();

  ctrlEnterExtension = keymap.of([
    {
      key: 'Ctrl-Enter',
      run: (view: EditorView) => {
        this.applySearch();
        return true;
      },
    },
  ]);

  constructor(
    private store: Store<AppState>,
    private cdr: ChangeDetectorRef,
    private objectCreatorService: ObjectCreatorService,
  ) {
    this.advancedSearchOpened$ = this.store.select(selectAdvancedSearchOpen);
    this.sidebarOpened$ = this.sidebarService.isOpen$;
    this.sidebarWide$ = this.sidebarService.isPanelWider$;
  }

  /* ========= Lifecycle ========= */

  ngOnInit() {
    // handles the very first binding too (harmless duplicate assignment)
    this.currentQuery = this.searchQuery || '';

    this.actions$
      .pipe(
        ofType(createAndAttachDimensionSuccess),
        tap(() => {
          this.removeFilter();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    // Initial snap without animating through intermediate heights
    this.clampNowNoAnimation();

    // Observe only the editor/content, not the resizable box to avoid feedback loops
    const editorEl: HTMLElement | undefined =
      this.queryEditor?.nativeElement ??
      this.queryEditor?.elRef?.nativeElement ??
      this.queryEditor?.hostEl;

    if (editorEl) {
      this.ro = new ResizeObserver(() => this.scheduleClamp());
      this.ro.observe(editorEl);
    }

    // Also react to window resizes (reflow changes lines/height)
    window.addEventListener('resize', this.scheduleClamp, { passive: true });

    // Snap once when the panel opens (false -> true)
    this.advancedSearchOpened$
      .pipe(
        distinctUntilChanged(),
        pairwise(),
        filter(([prev, curr]) => !prev && curr),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        // Wait for *ngIf content to be created & painted, then clamp once.
        queueMicrotask(() => this.clampNowNoAnimation());
        requestAnimationFrame(() => this.clampNowNoAnimation());
      });
  }

  ngOnDestroy(): void {
    this.ro?.disconnect();
    window.removeEventListener('resize', this.scheduleClamp as any);
  }

  /* ========= UI actions ========= */

  addHint(hint: string) {
    this.queryEditor.addOperator(hint);
  }

  openHints() {
    this.queryHintsOverlayService.open(this.componentId);
  }

  closeHints() {
    this.queryHintsOverlayService.closeIfOwner(this.componentId);
  }

  applySearch() {
    this.feedApi
      .validateQuery(
        this.feed.object_type_id,
        this.currentQuery,
        'editor',
        this.feed.id,
      )
      .subscribe({
        next: (response: QueryValidationResponse) => {
          if (this.currentQuery && this.currentQuery.length > 0) {
            this.addRecentSearchMutation.mutate({
              query_text: this.currentQuery,
            });
          }
          this.store.dispatch(searchPosts({ query: this.currentQuery }));
        },
        error: (err) => {
          this.queryEditor?.displayEditorError(
            err?.['response']?.line,
            err?.['response']?.message,
            err?.['response']?.column,
          );
          // Do not proceed
        },
      });
  }

  handleQueryChange(event: string) {
    this.currentQuery = event;
    // Debounced snap after CodeMirror reflows
    this.scheduleClamp();

    this.advancedSearchService.setQuery(event);
  }

  copyToClipboard() {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(this.currentQuery);
      this.copiedToClipboard = true;
      this.cdr.detectChanges();
      setTimeout(() => {
        this.copiedToClipboard = false;
        this.cdr.detectChanges();
      }, 1000);
    }
  }

  saveAsDimension() {
    this.objectCreatorService.createNewDimension(this.currentQuery);
  }

  attachAndSaveAsDimension() {
    const dimensionAttachParams: DimensionAttachParams = {
      object_id: this.feed.id,
      object_class_id: NomObjectClass.FEED,
      attach_type_id: DimensionAttachTypeId.FILTER,
    };

    this.objectCreatorService.createNewDimension(
      this.currentQuery,
      dimensionAttachParams,
    );
  }

  closeAdvancedSearch() {
    this.store.dispatch(toggleAdvancedSearch());
    this.queryHintsOverlayService.closeIfOwner(this.componentId);
  }

  /* ========= Drag resize ========= */

  startResize(event: MouseEvent) {
    event.preventDefault();
    const initialY = event.clientY;
    const boxEl = this.resizableBox.nativeElement as HTMLElement;
    const initialHeight = boxEl.offsetHeight;

    const mouseMove = (moveEvent: MouseEvent) => {
      const deltaY = moveEvent.clientY - initialY;
      const maxH = this.getMaxHeightPx(boxEl);
      const minH = this.getMinHeightPx(boxEl);
      const newHeight = Math.max(minH, Math.min(initialHeight + deltaY, maxH));
      // Disable transition just for the drag writes to avoid easing while dragging
      console.log('startResize newHieght', newHeight);
      this.withNoTransition(
        boxEl,
        () => (boxEl.style.height = `${newHeight}px`),
      );
    };

    const mouseUp = () => {
      window.removeEventListener('mousemove', mouseMove);
      window.removeEventListener('mouseup', mouseUp);
    };

    window.addEventListener('mousemove', mouseMove);
    window.addEventListener('mouseup', mouseUp);
  }

  /* ========= Clamp helpers ========= */

  // Debounce multiple observer events into a single write
  private scheduleClamp = () => {
    if (this.clampScheduled) return;
    this.clampScheduled = true;
    // Microtask is enough; if you prefer, swap with requestAnimationFrame
    queueMicrotask(() => {
      this.clampScheduled = false;
      this.clampNowNoAnimation();
    });
  };

  // Clamp once, without animating through intermediate heights
  private clampNowNoAnimation(): void {
    const el = this?.resizableBox?.nativeElement;
    if (el) {
      this.withNoTransition(el, () => this.clampResizableBoxHeight());
    }
  }

  private clampResizableBoxHeight(): void {
    const el = this.resizableBox.nativeElement;

    const contentHeight = this.getContentHeight(el);
    const maxH = this.getMaxHeightPx(el);
    const minH = this.getMinHeightPx(el);

    const target = Math.max(minH, Math.min(contentHeight, maxH));

    const current = el.style.height ? parseFloat(el.style.height) : NaN;
    if (!Number.isFinite(current) || Math.abs(current - target) > 0.5) {
      el.style.height = `${target}px`;
    }
  }

  private getContentHeight(el: HTMLElement): number {
    return el.clientHeight;
  }

  private getMaxHeightPx(el: HTMLElement): number {
    const cs = getComputedStyle(el);
    const v = cs.getPropertyValue('max-height');
    if (!v || v === 'none') return Number.POSITIVE_INFINITY;
    return this.toPx(v, el);
  }

  private getMinHeightPx(el: HTMLElement): number {
    const cs = getComputedStyle(el);
    const v = cs.getPropertyValue('min-height');
    if (!v || v === 'auto' || v === '0') return 0;
    return this.toPx(v, el);
  }

  private toPx(v: string, el: HTMLElement): number {
    const num = parseFloat(v);
    if (/px$/i.test(v) && Number.isFinite(num)) return num;

    const probe = document.createElement('div');
    probe.style.position = 'absolute';
    probe.style.visibility = 'hidden';
    probe.style.height = v;
    el.appendChild(probe);
    const px = probe.getBoundingClientRect().height;
    el.removeChild(probe);
    return px || 0;
  }

  // Temporarily disable CSS transitions while we set height
  private withNoTransition(el: HTMLElement, fn: () => void) {
    const prev = el.style.transition;
    el.style.transition = 'none';
    // Force style recompute so the 'none' takes effect
    void el.offsetHeight;
    try {
      fn();
    } finally {
      // Force style recompute again before restoring
      void el.offsetHeight;
      el.style.transition = prev;
    }
  }

  removeFilter() {
    this.queryEditor.updateEditorContent('');
    this.advancedSearchService.setQuery('');
    // this.store.dispatch(searchPosts({ query: this.searchQuery }));
    this.applySearch();
  }
}
